<template>
    <page :busy="loading" :tabs="tabs">
        <div class="advanced-date-block-page px-3 py-3">
            <validation-observer ref="form" v-slot="{ meta, setFieldValue, errors, handleSubmit }" validateOnMount>
                <b-form @submit.prevent="handleSubmit($event, save)">
                    <b-row>
                        <b-col cols="12">
                            <validation-provider name="reason" rules="required">
                                <form-textarea
                                    v-model="reason"
                                    floating-label="Enter reason for block"
                                    rows="9"
                                    max-rows="9"
                                    :left-icon="['fas', 'note-sticky']"
                                    :error="errors.reason"
                                    @input="(val) => setFieldValue('reason', val)"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <validation-provider name="startDate" rules="required">
                                <form-datepicker
                                    v-model="startDate"
                                    floating-label="Start date"
                                    @input="handleChangeStartDate"
                                    :error="errors.startDate"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <validation-provider name="endDate" rules="required">
                                <form-datepicker
                                    v-model="endDate"
                                    floating-label="End date"
                                    @input="handleChangeEndDate"
                                    :error="errors.endDate"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col md="12">
                            <validation-provider name="properties" rules="required">
                                <form-search-multiselect
                                    v-model="properties"
                                    class="properties-multiselect"
                                    :options="propertyOptions"
                                    :icon="['fas', 'hotel']"
                                    :responsiveness="false"
                                    floating-label="Properties"
                                    placeholder="Please select properties to block"
                                    :error="errors.properties"
                                    @input="(val) => setFieldValue('properties', val)"
                                />
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-button class="mt-3" type="submit" variant="primary" :disabled="loading || !meta.valid">
                        Save
                    </b-button>
                </b-form>
            </validation-observer>
        </div>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import isAfter from 'date-fns/isAfter';
    import isEqual from 'date-fns/isEqual';
    import isBefore from 'date-fns/isBefore';
    import addDays from 'date-fns/addDays';
    import parseISO from 'date-fns/parseISO';
    import API from '../../api';
    import { ADVANCED_DATE_BLOCK_PROPERTIES, ADVANCED_DATE_BLOCK_CREATE } from '../../endpoints';
    import Grid from '../generic/grid/grid.vue';
    import Page from '../generic/page/page.vue';
    import FormDatepicker from '../generic/form-datepicker/form-datepicker.vue';
    import FormTextarea from '../generic/form-textarea/form-textarea.vue';
    import FormSearchMultiselect from '../generic/form-search-multiselect/form-search-multiselect.vue';

    export default {
        name: 'CreateBlock',
        components: {
            Grid,
            Page,
            FormTextarea,
            FormDatepicker,
            FormSearchMultiselect,
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
            dateRange() {
                return {
                    from: this.startDate,
                    to: this.endDate,
                };
            },
        },
        data() {
            return {
                tabs: [
                    {
                        title: 'Create a Block',
                        route: '/app/advanced-date-block/block',
                        active: true,
                    },
                ],
                startDate: null,
                endDate: null,
                reason: '',
                properties: [],
                propertyOptions: [],
                loading: false,
                dateRequestFormat: 'yyyy-MM-dd',
            };
        },
        methods: {
            async save() {
                try {
                    this.loading = true;
                    await API.post(ADVANCED_DATE_BLOCK_CREATE(), {
                        start_date: this.startDate,
                        end_date: this.endDate,
                        reason: this.reason,
                        properties: this.properties.join(','),
                    });
                    this.startDate = null;
                    this.endDate = null;
                    this.properties = [];
                    this.propertyOptions = [];
                    this.reason = '';
                    this.successToast({
                        text: 'Blocks on selected properties successfully created',
                    });
                } catch (e) {
                    this.errorToast({ text: 'Something went wrong when blocking dates' });
                } finally {
                    this.loading = false;
                }
            },
            handleChangeStartDate(value) {
                if (value) {
                    const startDate = parseISO(value);
                    if (!this.endDate) {
                        this.endDate = format(addDays(startDate, 1), this.dateRequestFormat);
                    } else {
                        const endDate = parseISO(this.endDate);
                        if (isEqual(startDate, endDate) || isAfter(startDate, endDate)) {
                            this.endDate = format(addDays(startDate, 1), this.dateRequestFormat);
                        }
                    }
                    this.$refs.form.setFieldValue('startDate', this.startDate);
                    this.$refs.form.setFieldValue('endDate', this.endDate);
                }
            },
            handleChangeEndDate(value) {
                if (value) {
                    const endDate = parseISO(value);
                    if (!this.startDate) {
                        this.startDate = format(addDays(endDate, -1), this.dateRequestFormat);
                    } else {
                        const startDate = parseISO(this.startDate);
                        if (isEqual(endDate, startDate) || isBefore(endDate, startDate)) {
                            this.startDate = format(addDays(endDate, -1), this.dateRequestFormat);
                        }
                    }
                    this.$refs.form.setFieldValue('startDate', this.startDate);
                    this.$refs.form.setFieldValue('endDate', this.endDate);
                }
            },
            async fetchProperties() {
                try {
                    this.loading = true;
                    const { data } = await API.get(ADVANCED_DATE_BLOCK_PROPERTIES(), {
                        params: {
                            start_date: this.startDate,
                            end_date: this.endDate,
                        },
                    });
                    this.propertyOptions = data.map((item) => ({
                        value: item.id,
                        text: item.name,
                    }));
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch properties' });
                } finally {
                    this.loading = false;
                }
            },
        },
        watch: {
            dateRange: {
                handler(newVal) {
                    if (newVal.from && newVal.to) {
                        this.fetchProperties();
                    }
                },
                deep: true,
            },
        },
    };
</script>

<style lang="scss">
    .advanced-date-block-page {
        max-width: 900px;
        .properties-multiselect {
            .lx-label-control-icon {
                color: #212529 !important;
            }
        }
    }
</style>
